import {
  ContentMetricScoreByProductRecord,
  ContentScoreByWeekRecord,
  ContentScores,
  VoiceAndToneMatchType,
  VoiceAndToneScores
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import {
  ChartDataAdapter,
  Product,
  TableGridData,
  ToneScore,
  VoiceAndToneMatchAnalysisRow,
  VoiceScore
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import { VoiceAndToneDatum } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/VoiceAndTone/VoiceAndToneScatterPlot';

export class TableGridAdapter implements ChartDataAdapter<TableGridData> {
  primaryMetrics: ContentMetricScoreByProductRecord[];

  comparisonMetrics: ContentMetricScoreByProductRecord[];

  productRecords: Record<string, Product>;

  constructor(
    primaryMetrics: ContentMetricScoreByProductRecord[],
    comparisonMetrics: ContentMetricScoreByProductRecord[],
    productRecords: Record<string, Product>
  ) {
    this.primaryMetrics = primaryMetrics;
    this.comparisonMetrics = comparisonMetrics;
    this.productRecords = productRecords;
  }

  transform(): TableGridData {
    const getDifferences = (curr: ContentScores, prev: ContentScores) => {
      return Object.keys(curr).reduce((acc, key) => {
        acc[key] = {
          value: curr[key],
          prevValue: prev && prev[key],
          difference: prev && prev[key] ? curr[key] - prev[key] : 0
        };
        return acc;
      }, {});
    };

    try {
      const comparisonProductRecords = this.comparisonMetrics.reduce((acc, record) => {
        acc[record.stacklineSku] = record;
        return acc;
      }, {} as Record<string, ContentMetricScoreByProductRecord>);

      const mainProductRecords = this.primaryMetrics
        .filter((record) => comparisonProductRecords[record.stacklineSku])
        .map((record) => ({
          ...record,
          name: this.productRecords[record.stacklineSku].title,
          product: this.productRecords[record.stacklineSku],
          ...getDifferences(record.scores, comparisonProductRecords[record.stacklineSku].scores)
        }));

      return mainProductRecords;
    } catch (e) {
      console.error(e);
      return [];
    }
  }
}

export class ContentScoresByWeekAdapter implements ChartDataAdapter<TableGridData> {
  primaryMetrics: ContentScoreByWeekRecord[];

  comparisonMetrics: ContentScoreByWeekRecord[];

  constructor(primaryMetrics: ContentScoreByWeekRecord[], comparisonMetrics: ContentScoreByWeekRecord[]) {
    this.primaryMetrics = primaryMetrics;
    this.comparisonMetrics = comparisonMetrics;
  }

  transform(): TableGridData {
    const getWeekNumberFromWeekId = (weekId: number) => {
      return weekId.toString().slice(-2);
    };

    const getDifferences = (curr: ContentScores, prev: ContentScores) => {
      return Object.keys(curr).reduce((acc, key) => {
        acc[key] = {
          value: curr[key],
          prevValue: prev && prev[key],
          difference: prev && prev[key] ? curr[key] - prev[key] : 0
        };
        return acc;
      }, {});
    };

    try {
      const comparisonProductRecords = this.comparisonMetrics.reduce((acc, record) => {
        acc[getWeekNumberFromWeekId(record.weekId)] = record;
        return acc;
      }, {} as Record<string, ContentScoreByWeekRecord>);

      const mainProductRecords = this.primaryMetrics
        .sort((a, b) => b.weekId - a.weekId)
        .map((record) => {
          return {
            ...record,
            ...getDifferences(record.scores, comparisonProductRecords[getWeekNumberFromWeekId(record.weekId)]?.scores)
          };
        });

      return mainProductRecords;
    } catch (e) {
      console.error('Error parsing response in TableGridAdapter: ', e);
      return [];
    }
  }
}

const isMatch = (score: VoiceScore | ToneScore) => {
  const { expected, actual } = score;
  return expected === actual;
};

const getMatchType = (voice: VoiceScore, tone: ToneScore) => {
  const isVoiceMatch = isMatch(voice);
  const isToneMatch = isMatch(tone);

  if (isVoiceMatch && isToneMatch) {
    return VoiceAndToneMatchType.FullMatch;
  } else if (isVoiceMatch) {
    return VoiceAndToneMatchType.ToneMismatch;
  } else if (isToneMatch) {
    return VoiceAndToneMatchType.VoiceMismatch;
  } else {
    return VoiceAndToneMatchType.FullMismatch;
  }
};

const getScatterPlotIcon = (record: VoiceAndToneMatchAnalysisRow) => {
  const { voice, tone } = record;
  const isVoiceMatch = isMatch(voice);
  const isToneMatch = isMatch(tone);
  let dotColor: string;

  if (isVoiceMatch && isToneMatch) {
    dotColor = 'green';
  } else if (isVoiceMatch) {
    dotColor = 'purple';
  } else if (isToneMatch) {
    dotColor = 'orange';
  } else {
    dotColor = 'red';
  }

  const disabledIcon = `muted${dotColor.charAt(0).toUpperCase()}${dotColor.slice(1)}`;
  return {
    icon: `url(/svg/${dotColor}Dot.svg)`,
    disabledIcon: `url(/svg/${disabledIcon}Dot.svg)`
  };
};

const getVoiceAndToneScatterPlotData = (
  record: VoiceAndToneMatchAnalysisRow
): {
  matchType: VoiceAndToneMatchType;
  icon: string;
  disabledIcon: string;
  x: number;
  y: number;
} => {
  return {
    x: record.voice.score * 100,
    y: record.tone.score * 100,
    matchType: getMatchType(record.voice, record.tone),
    ...getScatterPlotIcon(record)
  };
};

export class VoiceAndToneMatchAnalysisAdapter implements ChartDataAdapter<VoiceAndToneMatchAnalysisRow[]> {
  primaryMetrics: ContentMetricScoreByProductRecord[];

  comparisonMetrics: ContentMetricScoreByProductRecord[];

  productRecords: Record<string, Product>;

  constructor(
    primaryMetrics: ContentMetricScoreByProductRecord[],
    comparisonMetrics: ContentMetricScoreByProductRecord[],
    productRecords: Record<string, Product>
  ) {
    this.primaryMetrics = primaryMetrics;
    this.comparisonMetrics = comparisonMetrics;
    this.productRecords = productRecords;
  }

  transform(): VoiceAndToneMatchAnalysisRow[] {
    const getDifferences = (curr: ContentScores, prev: ContentScores) => {
      return Object.keys(curr).reduce((acc, key) => {
        if (key === 'voiceMatchScore' || key === 'toneMatchScore') {
          acc[key] = {
            value: curr[key],
            prevValue: prev && prev[key],
            difference: prev && prev[key] ? curr[key] - prev[key] : 0
          };
        }
        return acc;
      }, {});
    };

    try {
      const comparisonProductRecords = this.comparisonMetrics.reduce((acc, record) => {
        acc[record.stacklineSku] = record;
        return acc;
      }, {} as Record<string, ContentMetricScoreByProductRecord>);

      const mainProductRecords = this.primaryMetrics
        .filter((record) => comparisonProductRecords[record.stacklineSku])
        .map((record) => ({
          ...record,
          name: this.productRecords[record.stacklineSku].title,
          product: this.productRecords[record.stacklineSku],
          ...getDifferences(record.scores, comparisonProductRecords[record.stacklineSku].scores)
        }))
        .map((record) => {
          return {
            stacklineSku: record.stacklineSku,
            product: record.product,
            // FIXME: Ignoring the types errors but they are correct in the backend
            // @ts-ignore
            voiceMatchScore: record.voiceMatchScore,
            // @ts-ignore
            toneMatchScore: record.toneMatchScore,
            voice: {
              score: (record.scores as VoiceAndToneScores).voiceMatchScore,
              expected: (record.scores as VoiceAndToneScores).expectedVoice,
              actual: (record.scores as VoiceAndToneScores).currentDominantVoiceType
            },
            tone: {
              score: (record.scores as VoiceAndToneScores).toneMatchScore,
              expected: (record.scores as VoiceAndToneScores).expectedTone,
              actual: (record.scores as VoiceAndToneScores).currentDominantToneType
            }
          };
        })
        .map((record) => {
          return {
            ...record,
            matchType: getMatchType(record.voice, record.tone)
          };
        })
        // filter out products with no voice or tone match scores, as they are likely data-related issues
        .filter((record) => record.voice.score !== 0 && record.tone.score !== 0);

      return mainProductRecords;
    } catch (e) {
      console.error(e);
      return [];
    }
  }
}

export class VoiceAndToneScatterPlotAdapter implements ChartDataAdapter<VoiceAndToneDatum[]> {
  voiceAndToneTableGridAdapter: TableGridAdapter;

  constructor(voiceAndToneTableGridAdapter: TableGridAdapter) {
    this.voiceAndToneTableGridAdapter = voiceAndToneTableGridAdapter;
  }

  transform(): VoiceAndToneDatum[] {
    const tableGridData = this.voiceAndToneTableGridAdapter.transform().slice(0, 500);

    const withScatterPlotData = tableGridData.map((record: VoiceAndToneMatchAnalysisRow) => {
      return {
        ...record,
        ...getVoiceAndToneScatterPlotData(record)
      };
    });

    return withScatterPlotData;
  }
}
